<template>
  <div v-if="loaded" class="internal-tools-menu">
    <button
      type="button"
      class="tools-menu-trigger"
      title="More"
      aria-haspopup="menu"
      :aria-expanded="menuOpen"
      @click.stop="toggleMenu"
    >
      <i class="fas fa-ellipsis-h"></i>
    </button>

    <Teleport to="body">
      <div
        v-if="menuOpen"
        class="tools-menu-backdrop"
        @click="closeMenu"
      ></div>
      <div
        v-if="menuOpen"
        class="tools-menu-popover"
        :style="popoverStyle"
        role="menu"
        @click.stop
      >
        <div class="tools-menu-version" role="none">
          <i class="fas fa-code-branch"></i>
          <span>v{{ version }}</span>
          <span class="tools-menu-env">{{ environment }}</span>
        </div>
        <button
          type="button"
          class="tools-menu-item"
          role="menuitem"
          @click="openWhatsNew"
        >
          <i class="fas fa-rocket"></i>
          What's New
        </button>
        <button
          type="button"
          class="tools-menu-item"
          role="menuitem"
          @click="openFeedback"
        >
          <i class="fas fa-comment-dots"></i>
          Feedback
        </button>
      </div>
    </Teleport>

    <WhatsNewModal
      v-if="showWhatsNew"
      @close="showWhatsNew = false"
    />

    <FeedbackModal
      v-if="showFeedback"
      @close="showFeedback = false"
      @submitted="handleFeedbackSubmitted"
    />
  </div>
</template>

<script>
import observabilityAPI from '@/services/observabilityAPI';
import WhatsNewModal from './WhatsNewModal.vue';
import FeedbackModal from './FeedbackModal.vue';

export default {
  name: 'InternalFooter',

  components: {
    WhatsNewModal,
    FeedbackModal,
  },

  data() {
    return {
      version: '',
      environment: '',
      showWhatsNew: false,
      showFeedback: false,
      loaded: false,
      menuOpen: false,
      popoverStyle: {},
    };
  },

  async mounted() {
    await this.loadVersionInfo();
    window.addEventListener('resize', this.positionPopover);
    window.addEventListener('scroll', this.positionPopover, true);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.positionPopover);
    window.removeEventListener('scroll', this.positionPopover, true);
  },

  methods: {
    async loadVersionInfo() {
      try {
        const info = await observabilityAPI.getVersionInfo();
        this.version = info.version;
        this.environment = info.environment;
        this.loaded = true;
      } catch (error) {
        console.error('Failed to load version info:', error);
      }
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      if (this.menuOpen) {
        this.$nextTick(() => this.positionPopover());
      }
    },

    closeMenu() {
      this.menuOpen = false;
    },

    positionPopover() {
      if (!this.menuOpen) return;
      const trigger = this.$el?.querySelector('.tools-menu-trigger');
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const menuWidth = 196;
      const menuHeight = 132;
      const gap = 8;
      const pad = 12;

      let left = rect.right - menuWidth;
      let top = rect.top - menuHeight - gap;

      left = Math.max(pad, Math.min(left, window.innerWidth - menuWidth - pad));
      if (top < pad) {
        top = rect.bottom + gap;
      }

      this.popoverStyle = {
        top: `${top}px`,
        left: `${left}px`,
      };
    },

    openWhatsNew() {
      this.closeMenu();
      this.showWhatsNew = true;
    },

    openFeedback() {
      this.closeMenu();
      this.showFeedback = true;
    },

    handleFeedbackSubmitted() {
      this.showFeedback = false;
    },
  },
};
</script>

<style scoped>
.internal-tools-menu {
  flex-shrink: 0;
}

.tools-menu-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: var(--border-radius, 8px);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.tools-menu-trigger:hover {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.28);
}

.tools-menu-trigger i {
  font-size: 14px;
}
</style>

<style>
.tools-menu-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1090;
}

.tools-menu-popover {
  position: fixed;
  z-index: 1100;
  width: 196px;
  padding: 6px;
  background: #ffffff;
  border: 1px solid #d9e2ef;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(33, 41, 56, 0.14);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tools-menu-version {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  font-size: 11px;
  color: #6b7a99;
  font-family: 'Monaco', 'Consolas', monospace;
  border-bottom: 1px solid #d9e2ef;
  margin-bottom: 2px;
}

.tools-menu-version i {
  color: #2db385;
  font-size: 10px;
}

.tools-menu-env {
  margin-left: auto;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.tools-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #212938;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;
}

.tools-menu-item:hover {
  background: #e0efff;
}

.tools-menu-item i {
  width: 14px;
  text-align: center;
  color: #0059b2;
}

.tools-menu-item .fa-rocket {
  color: #008ca6;
}
</style>
